import { useRef } from 'react';
import { Input } from '../index';
import Calendar, { useCalendar, useRangeCalendar } from './Calendar';
import { getFormattedDate } from '../../../utilities/DateUtils';
import { useFlagState } from '../../../utilities/CustomHooks';
import { Popover } from '@mui/material';
import { useController } from 'react-hook-form';
import { CalendarIcon } from 'assets/common';
import { isNullOrUndefined } from 'utilities/Utils';

export const migrateDefaultValue = (dateObj: { from: Date; to: Date }) => {
  return {
    from: dateObj?.from ? new Date(dateObj.from) : null,
    to: dateObj?.to ? new Date(dateObj.to) : null,
  };
};

type CalendarInputProps = {
  popoverState: {
    isCalendarOpen: boolean;
    showCalendar: Function;
    hideCalendar: Function;
  };
  calendarProps: {
    handleDayClick: Function;
    // in normal calendars
    selectedDays?: Date | Date[];
    // in case of range calendar
    from?: Date;
    to?: Date;
  };
  showApplyBtn?: boolean;
  showTodayButton?: boolean;
  todayBtnText?: 'Today' | 'This Week';
  disabledBefore?: Date;
  disabledAfter?: Date;
  disabled?: boolean;
  error?: boolean;
  placeholder?: string;
  isRangeCalendar?: boolean;
  showYearChangeIcon?: boolean;
  inputDateFormat?: string;
  className?: string;
  inputRef?;
};

const CalendarInput = (props: CalendarInputProps) => {
  const {
    popoverState,
    calendarProps,
    showApplyBtn = false,
    showTodayButton = false,
    todayBtnText = 'Today',
    isRangeCalendar = false,
    disabledBefore,
    disabledAfter,
    disabled = false,
    error = false,
    placeholder = isRangeCalendar ? 'Select a range' : 'Enter Date',
    showYearChangeIcon = false,
    inputDateFormat = 'DD MMM YYYY, ddd',
    className = '',
    inputRef = null,
  } = props;

  const containerRef = useRef();

  const { isCalendarOpen, showCalendar, hideCalendar } = popoverState;
  const { selectedDays } = calendarProps;

  let inputVal = '';
  if (isRangeCalendar) {
    const { from, to } = calendarProps;
    inputVal = `${from ? `${getFormattedDate(from, inputDateFormat)} - ` : ''}${
      to ? getFormattedDate(to, inputDateFormat) : ''
    }`;
  } else {
    inputVal =
      selectedDays &&
      (Array.isArray(selectedDays)
        ? `${selectedDays.length} dates are selected`
        : getFormattedDate(selectedDays, inputDateFormat));
  }

  return (
    <div className={className} ref={containerRef}>
      <Input
        ref={inputRef}
        value={inputVal ?? ''}
        onClick={
          !disabled &&
          (showCalendar as unknown as React.MouseEventHandler<HTMLDivElement>)
        }
        disabled={disabled}
        error={error}
        rightElement={<img src={CalendarIcon} alt="calendar-icon" />}
        placeholder={placeholder}
        readOnly={true}
        // style={{ width: '250px' }}
        style={{ cursor: 'pointer' }} //classes we are not taking as a parameter thatswhy using style
      />
      <Popover
        open={isCalendarOpen}
        anchorEl={containerRef.current}
        onClose={
          hideCalendar as unknown as React.MouseEventHandler<HTMLDivElement>
        }
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transitionDuration={'auto'}
      >
        <Calendar
          {...calendarProps}
          showApplyBtn={showApplyBtn}
          showTodayButton={showTodayButton}
          isRangeCalendar={isRangeCalendar}
          disabledBefore={disabledBefore}
          disabledAfter={disabledAfter}
          todayBtnText={todayBtnText}
          hideCalendar={popoverState.hideCalendar}
          showYearChangeIcon={showYearChangeIcon}
        />
      </Popover>
    </div>
  );
};

export function useCalendarInput({
  initialDate,
  acceptMultipleDates = false,
  callback = () => {},
  multiSelectRestriction = {},
  defaultNull = false,
}: {
  initialDate;
  acceptMultipleDates?: boolean;
  callback?: (selectedDay?: Date | Date[]) => void;
  multiSelectRestriction?: {};
  defaultNull?: boolean;
}) {
  const [isCalendarOpen, showCalendar, hideCalendar] = useFlagState(false);

  function callbackHandler(newSelectedDays) {
    hideCalendar();
    //@ts-ignore
    callback(newSelectedDays ?? initialDate);
  }

  const calendarProps = useCalendar(
    initialDate,
    acceptMultipleDates,
    //@ts-ignore
    callbackHandler,
    multiSelectRestriction,
    { month: {}, year: {}, count: 0 },
    () => {},
    defaultNull,
  );

  const popoverState = {
    isCalendarOpen,
    showCalendar,
    hideCalendar,
    callbackHandler,
  };

  return {
    popoverState,
    calendarProps,
  };
}

export function useRangeCalendarInput({
  initialDate = { from: null, to: null },
  callback = () => {},
  showApplyBtn = false,
}: {
  initialDate?: { from: Date; to: null };
  callback?: (val) => void;
  showApplyBtn?: boolean;
}) {
  const [isCalendarOpen, showCalendar, hideCalendar] = useFlagState(false);
  const _initialDate = [initialDate?.from, initialDate?.to];

  const callbackHandler = val => {
    if (val?.to && !showApplyBtn) hideCalendar();
    callback(val);
  };
  const calendarProps = useRangeCalendar(
    _initialDate,
    callbackHandler,
    {},
    'legacy',
  );

  const popoverState = {
    isCalendarOpen,
    showCalendar,
    hideCalendar,
  };

  return {
    popoverState,
    calendarProps,
  };
}

const ControlledCalendarInput = props => {
  const {
    name,
    control,
    defaultValue,
    isRequired = false,
    customValidator,
    isRangeCalendar = false,
    showApplyBtn = false,
    ...rest
  } = props;
  const validations = {
    validate: inputValue => {
      if (customValidator) {
        return customValidator(inputValue);
      } else if (isRequired) {
        if (
          isNullOrUndefined(inputValue) ||
          (isRangeCalendar &&
            (inputValue.from === null || inputValue.to === null))
        ) {
          return 'This field is required';
        }
      }
      return true;
    },
  };

  if (typeof customValidator === 'function') {
    validations.validate = customValidator;
  }

  const {
    field: { onChange, value },
  } = useController({
    name,
    rules: validations,
    control,
    defaultValue,
  });

  if (isRangeCalendar) {
    const rangeCalendarInput = useRangeCalendarInput({
      initialDate: value,
      callback: onChange,
      showApplyBtn,
    });
    return <CalendarInput {...rangeCalendarInput} isRangeCalendar {...rest} />;
  }
  const CalendarInputProps = useCalendarInput({
    initialDate: value && new Date(value),
    callback: onChange,
    multiSelectRestriction: {},
    defaultNull: true,
  });

  return <CalendarInput {...CalendarInputProps} {...rest} />;
};

export { ControlledCalendarInput };

export default CalendarInput;
